import { useCallback, useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { getBillingStatements, type BillingStatement } from "@/api";
import { formatCompactNumber, formatUsdt, isPositiveUsdt } from "@/utils/money";
import { DataTableLoadingOverlay } from "../components/data-table/DataTableLoadingOverlay";
import { DataTableStateRow } from "../components/data-table/DataTableStateRow";
import { TablePaginationFooter } from "../components/data-table/TablePaginationFooter";

const BILLING_PAGE_SIZE = 10;
const BILLING_COL_COUNT = 5;

function formatBillingPeriod(period: string, locale: string | undefined): string {
  const match = /^(\d{4})-(\d{2})$/.exec(period);
  if (!match) return period;

  const year = Number(match[1]);
  const monthIndex = Number(match[2]) - 1;
  if (!Number.isInteger(year) || !Number.isInteger(monthIndex)) return period;

  return new Intl.DateTimeFormat(locale || "en", {
    month: "long",
    year: "numeric",
    timeZone: "UTC",
  }).format(new Date(Date.UTC(year, monthIndex, 1)));
}

function formatBillingUsdt(value: string): string {
  return `${formatUsdt(value, { maxDecimals: 6 })} USDT`;
}

export function BillingStatements() {
  const { t, i18n } = useTranslation();
  const [statements, setStatements] = useState<BillingStatement[]>([]);
  const [page, setPage] = useState(1);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const totalPages = Math.max(1, Math.ceil(total / BILLING_PAGE_SIZE));

  const refreshStatements = useCallback(async (nextPage: number = 1) => {
    setLoading(true);
    setError(null);
    try {
      const res = await getBillingStatements({
        page: nextPage,
        limit: BILLING_PAGE_SIZE,
      });
      if (res.ok) {
        setStatements(res.data.list);
        setTotal(res.data.total);
        setPage(res.data.page);
      } else {
        setError(res.error.message);
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    void refreshStatements(1);
  }, [refreshStatements]);

  return (
    <div className="space-y-8">
      <div className="flex min-h-[57px] flex-col gap-1">
        <div>
          <h1 className="text-2xl font-bold leading-8 text-slate-900 dark:text-white">
            {t("billing.title")}
          </h1>
          <p className="mt-1 text-sm leading-[21px] text-slate-500 dark:text-[#90A1B9]">
            {t("billing.subtitle")}
          </p>
        </div>
      </div>

      {error ? (
        <div className="rounded-2xl border border-red-200 bg-red-50 px-5 py-4 text-sm text-red-700 dark:border-red-500/20 dark:bg-red-500/10 dark:text-red-300">
          {t("billing.loadFailed")}
        </div>
      ) : null}

      <div className="overflow-hidden rounded-2xl border border-slate-200 bg-slate-50 dark:border-[#1D293D] dark:bg-[#1A1D1F]">
        <div className="border-b border-slate-200 p-4 dark:border-[#1D293D]">
          <h2 className="text-lg font-bold leading-7 text-slate-900 dark:text-white">
            {t("billing.statementHistory")}
          </h2>
        </div>

        <div className="relative">
          <DataTableLoadingOverlay loading={loading && statements.length > 0} />
          <div className="overflow-x-auto custom-scrollbar">
            <table className="min-w-full border-collapse">
              <thead className="bg-slate-50 dark:bg-[#0E0F11]">
                <tr>
                  <th className="w-full whitespace-nowrap border-b border-slate-200 py-4 pl-6 pr-2 text-left text-xs font-medium text-slate-500 dark:border-[#1D293D] dark:text-[#90A1B9]">
                    {t("billing.colPeriod")}
                  </th>
                  <th className="w-[1%] whitespace-nowrap border-b border-slate-200 px-2 py-4 text-left text-xs font-medium text-slate-500 dark:border-[#1D293D] dark:text-[#90A1B9]">
                    {t("billing.colTotalPayment")}
                  </th>
                  <th className="w-[1%] whitespace-nowrap border-b border-slate-200 px-2 py-4 text-left text-xs font-medium text-slate-500 dark:border-[#1D293D] dark:text-[#90A1B9]">
                    {t("billing.colSubscriptionFee")}
                  </th>
                  <th className="w-[1%] whitespace-nowrap border-b border-slate-200 px-2 py-4 text-left text-xs font-medium text-slate-500 dark:border-[#1D293D] dark:text-[#90A1B9]">
                    {t("billing.colApiUsageFee")}
                  </th>
                  <th className="w-[1%] whitespace-nowrap border-b border-slate-200 py-4 pl-2 pr-6 text-right text-xs font-medium text-slate-500 dark:border-[#1D293D] dark:text-[#90A1B9]">
                    {t("billing.colTotalCredits")}
                  </th>
                </tr>
              </thead>
              <tbody>
                <DataTableStateRow
                  colSpan={BILLING_COL_COUNT}
                  loading={loading && statements.length === 0}
                  isEmpty={!loading && statements.length === 0}
                  emptyTitle={t("billing.emptyTitle")}
                  emptyHint={t("billing.emptyHint")}
                />
                {statements.map((item) => (
                  <tr key={item.id} className="transition-colors hover:bg-white dark:hover:bg-[#D2F0E1]/5">
                    <td className="whitespace-nowrap border-b border-slate-200 py-5 pl-6 pr-2 text-xs font-medium text-slate-900 dark:border-[#1D293D] dark:text-[#E2E8F0]">
                      {formatBillingPeriod(item.period, i18n.resolvedLanguage)}
                    </td>
                    <td className="whitespace-nowrap border-b border-slate-200 px-2 py-5 text-sm font-medium text-emerald-600 dark:border-[#1D293D] dark:text-[#00FFA3]">
                      {formatBillingUsdt(item.totalPayment)}
                    </td>
                    <td className="whitespace-nowrap border-b border-slate-200 px-2 py-5 text-sm font-medium text-slate-500 dark:border-[#1D293D] dark:text-[#90A1B9]">
                      {formatBillingUsdt(item.subscriptionFee)}
                    </td>
                    <td className={`whitespace-nowrap border-b border-slate-200 px-2 py-5 text-sm font-medium dark:border-[#1D293D] ${
                      isPositiveUsdt(item.apiUsageFee)
                        ? "text-orange-600 dark:text-[#F17313]"
                        : "text-slate-500 dark:text-[#90A1B9]"
                    }`}>
                      {formatBillingUsdt(item.apiUsageFee)}
                    </td>
                    <td className="whitespace-nowrap border-b border-slate-200 py-5 pl-2 pr-6 text-right text-xs font-medium text-slate-500 dark:border-[#1D293D] dark:text-[#90A1B9]">
                      {formatCompactNumber(item.totalCredits, {
                        locale: i18n.resolvedLanguage,
                      })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>

        <div className="border-b border-slate-200 px-4 py-4 text-center dark:border-[#1D293D]">
          <p className="text-xs font-medium leading-7 text-slate-500 dark:text-[#90A1B9]">
            {t("billing.note")}
          </p>
        </div>

        {statements.length > 0 ? (
          <TablePaginationFooter
            page={page}
            totalPages={totalPages}
            totalItems={total}
            onPageChange={(nextPage) => void refreshStatements(nextPage)}
            className="mx-4 mb-4"
          />
        ) : null}
      </div>
    </div>
  );
}
